import React, { useState, useEffect, useCallback } from 'react';
import api from '../utils/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Checkbox } from './ui/checkbox';
import { Switch } from './ui/switch';
import { Loader2, Send, Users } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { useAuth } from '../context/AuthContext';
import { canManageFeature } from '../utils/featurePermissions';

export default function WhatsappBroadcast() {
  const { user } = useAuth();
  const { toast } = useToast();
  const canSend = canManageFeature(user, 'announcements');

  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [sendToAll, setSendToAll] = useState(true);
  const [emergency, setEmergency] = useState(false);
  const [classes, setClasses] = useState([]);
  const [sections, setSections] = useState([]);
  const [selectedClassIds, setSelectedClassIds] = useState([]);
  const [selectedSectionIds, setSelectedSectionIds] = useState([]);
  const [previewCount, setPreviewCount] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    const schoolId = localStorage.getItem('school_id');
    if (!schoolId) return;
    api.get('/classes', { params: { school_id: schoolId } })
      .then((res) => setClasses(res.data?.data?.classes || res.data?.data || []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (selectedClassIds.length !== 1) {
      setSections([]);
      return;
    }
    api.get(`/classes/${selectedClassIds[0]}/sections`)
      .then((res) => setSections(res.data?.data?.sections || res.data?.data || []))
      .catch(() => setSections([]));
  }, [selectedClassIds]);

  const loadPreview = useCallback(async () => {
    setLoadingPreview(true);
    try {
      const params = {
        send_to_all: sendToAll ? '1' : '0',
        class_ids: selectedClassIds,
        section_ids: selectedSectionIds,
      };
      const res = await api.get('/whatsapp/broadcast/preview', { params });
      setPreviewCount(res.data?.data?.count ?? 0);
    } catch {
      setPreviewCount(null);
      toast({ title: 'Error', description: 'Could not load recipient count', variant: 'destructive' });
    } finally {
      setLoadingPreview(false);
    }
  }, [sendToAll, selectedClassIds, selectedSectionIds, toast]);

  useEffect(() => {
    if (canSend) loadPreview();
  }, [canSend, loadPreview]);

  const toggleClass = (id) => {
    setSendToAll(false);
    setSelectedClassIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const toggleSection = (id) => {
    setSendToAll(false);
    setSelectedSectionIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const handleSend = async () => {
    if (!title.trim() || !message.trim()) {
      toast({ title: 'Required', description: 'Title and message are required', variant: 'destructive' });
      return;
    }
    setSending(true);
    try {
      const res = await api.post('/whatsapp/broadcast', {
        title: title.trim(),
        message: message.trim(),
        send_to_all: sendToAll,
        class_ids: sendToAll ? [] : selectedClassIds,
        section_ids: sendToAll ? [] : selectedSectionIds,
        emergency,
      });
      const queued = res.data?.data?.queued ?? 0;
      toast({ title: 'Queued', description: `${queued} WhatsApp message(s) queued for delivery` });
      setTitle('');
      setMessage('');
    } catch (e) {
      toast({ title: 'Error', description: e.response?.data?.message || 'Failed to queue broadcast', variant: 'destructive' });
    } finally {
      setSending(false);
    }
  };

  if (!canSend) {
    return (
      <div className="p-6 text-sm text-muted-foreground">You do not have permission to send WhatsApp broadcasts.</div>
    );
  }

  return (
    <div className="space-y-6 max-w-3xl">
      <div>
        <h1 className="text-2xl font-semibold text-foreground">WhatsApp Broadcast</h1>
        <p className="text-sm text-muted-foreground mt-1">Messages are queued and sent via the background worker — not sent directly from the browser.</p>
      </div>

      <Card className="rounded-2xl">
        <CardHeader>
          <CardTitle className="text-lg">Compose message</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label>Title</Label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} className="mt-1.5" placeholder="School notice title" />
          </div>
          <div>
            <Label>Message</Label>
            <Textarea value={message} onChange={(e) => setMessage(e.target.value)} className="mt-1.5 min-h-[120px]" placeholder="Message body for parents…" />
          </div>
          <div className="flex items-center justify-between rounded-lg border p-4">
            <div>
              <p className="font-medium">Emergency notice</p>
              <p className="text-xs text-muted-foreground">Uses the emergency template for urgent alerts.</p>
            </div>
            <Switch checked={emergency} onCheckedChange={setEmergency} />
          </div>
        </CardContent>
      </Card>

      <Card className="rounded-2xl">
        <CardHeader>
          <CardTitle className="text-lg">Recipients</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-2">
            <Checkbox id="send-all" checked={sendToAll} onCheckedChange={(v) => setSendToAll(!!v)} />
            <Label htmlFor="send-all">Send to all parents in school</Label>
          </div>
          {!sendToAll && (
            <>
              <div>
                <Label className="mb-2 block">Classes</Label>
                <div className="flex flex-wrap gap-2">
                  {classes.map((c) => (
                    <Button
                      key={c.id}
                      type="button"
                      size="sm"
                      variant={selectedClassIds.includes(c.id) ? 'default' : 'outline'}
                      onClick={() => toggleClass(c.id)}
                    >
                      {c.name}
                    </Button>
                  ))}
                </div>
              </div>
              {sections.length > 0 && (
                <div>
                  <Label className="mb-2 block">Sections</Label>
                  <div className="flex flex-wrap gap-2">
                    {sections.map((s) => (
                      <Button
                        key={s.id}
                        type="button"
                        size="sm"
                        variant={selectedSectionIds.includes(s.id) ? 'default' : 'outline'}
                        onClick={() => toggleSection(s.id)}
                      >
                        {s.name}
                      </Button>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Users className="w-4 h-4" />
            {loadingPreview ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <span>{previewCount ?? '—'} parent recipient(s) estimated</span>
            )}
            <Button type="button" variant="ghost" size="sm" onClick={loadPreview}>Refresh</Button>
          </div>
        </CardContent>
      </Card>

      <Button onClick={handleSend} disabled={sending} className="bg-blue-600 hover:bg-blue-700 text-white">
        {sending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
        Queue broadcast
      </Button>
    </div>
  );
}
